import { User } from '@/redux/auth/authTypes';
import { useEffect, useState } from 'react';

// Define request status states
export type RequestStatus = 'none' | 'pending' | 'approved' | 'rejected';

// Define request types
export type RequestType = 'upgrade' | 'trial';

// Define persistent state interface
export interface RequestState {
  status: RequestStatus;
  type: RequestType | null;
  userId: string | undefined;
  date: string | null;
}

// Storage key
const STORAGE_KEY = 'subscription_request_state';

/**
 * Save request state to localStorage
 */
export const saveRequestState = (state: RequestState): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

/**
 * Load request state from localStorage
 */
export const loadRequestState = (): RequestState | null => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return null;

  try {
    return JSON.parse(saved) as RequestState;
  } catch (e) {
    console.error('Failed to parse saved request state', e);
    return null;
  }
};

/**
 * Clear request state from localStorage
 */
export const clearRequestState = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

/**
 * Hook to manage subscription request state with localStorage persistence
 */
const useSubscriptionRequestState = (user: User | null) => {
  const [requestStatus, setRequestStatus] = useState<RequestStatus>('none');
  const [requestType, setRequestType] = useState<RequestType | null>(null);
  const [requestDate, setRequestDate] = useState<Date | null>(null);

  // Initialize state from localStorage on component mount
  useEffect(() => {
    if (!user) return;

    const savedState = loadRequestState();

    if (savedState && savedState.userId === user._id) {
      setRequestStatus(savedState.status);
      setRequestType(savedState.type);
      setRequestDate(savedState.date ? new Date(savedState.date) : null);
    } else if (savedState && savedState.userId !== user._id) {
      // Clear if user ID doesn't match (different user logged in)
      clearRequestState();
    }
  }, [user]);

  // Update localStorage when state changes
  useEffect(() => {
    if (!user) return;

    if (requestStatus === 'none') {
      clearRequestState();
      return;
    }

    const stateToSave: RequestState = {
      status: requestStatus,
      type: requestType,
      userId: user._id,
      date: requestDate ? requestDate.toISOString() : null,
    };

    saveRequestState(stateToSave);
  }, [requestStatus, requestType, requestDate, user]);

  // Set request to pending
  const setRequestPending = (type: RequestType): void => {
    setRequestType(type);
    setRequestStatus('pending');
    setRequestDate(new Date());
  };

  // Reset request state
  const resetRequestState = (): void => {
    setRequestStatus('none');
    setRequestType(null);
    setRequestDate(null);
    clearRequestState();
  };

  // Check if a request is pending
  const isRequestPending = (): boolean => {
    return requestStatus === 'pending';
  };

  return {
    requestStatus,
    requestType,
    requestDate,
    setRequestPending,
    resetRequestState,
    isRequestPending,
  };
};

export default useSubscriptionRequestState;
